import { useMutation, useQueryClient } from '@tanstack/react-query';
import { agreeMarketingAlarm } from 'shared/src/api/myPageService/agreeMarketingAlarm';
import { disagreeMarketingAlarm } from 'shared/src/api/myPageService/disagreeMarketingAlarm';
import { queryKey } from '../../../constant/queryKey';
import type { GetMemberInfoResponse } from 'shared/src/api/getMemberInfo/types';
import { bridge } from 'shared/src/utils';

type MarketingType = 'SMS' | 'PUSH' | 'NIGHT_PUSH';

type ToggleMarketingParams = {
  type: MarketingType;
  isAgreed: boolean;
};

export const useToggleMarketingAlarmSetting = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ type, isAgreed }: ToggleMarketingParams) => {
      if (isAgreed) {
        return await disagreeMarketingAlarm(type);
      } else {
        if (type === 'NIGHT_PUSH') {
          await agreeMarketingAlarm('PUSH');
        }
        return await agreeMarketingAlarm(type);
      }
    },

    onSuccess: async (_, { type, isAgreed }) => {
      const old = client.getQueryData<GetMemberInfoResponse>(
        queryKey.marketingAlarmSettings(),
      );
      if (!old) return;

      const agreedKeyMap: Record<
        MarketingType,
        keyof GetMemberInfoResponse
      > = {
        SMS: 'sms_agreed_at',
        PUSH: 'push_agreed_at',
        NIGHT_PUSH: 'night_push_agreed_at',
      };

      const key = agreedKeyMap[type];

      const updated: GetMemberInfoResponse = {
        ...old,
        [key]: isAgreed ? null : new Date().toISOString(),
      };

      // NIGHT_PUSH를 ON할 때는 PUSH도 같이 ON 처리
      if (type === 'NIGHT_PUSH' && !isAgreed) {
        updated['push_agreed_at'] = new Date().toISOString();
      }

      // 캐시 업데이트
      client.setQueryData(queryKey.marketingAlarmSettings(), updated);

      // 네이티브 member 동기화
      const jsonString = JSON.stringify({ member: updated });
      await bridge.updateMemberMe(jsonString);
    },
  });
};
